/**
 * Authenticated API Client with Firebase Token Interceptor
 * =============================================================================
 * Extends the base API client to automatically attach Firebase ID tokens
 * to all requests
 *
 * Features:
 * - Automatic token attachment via request interceptor
 * - Automatic token refresh on 401 errors
 * - Handles token expiration gracefully
 * - Redirects to login on auth failures
 */

import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { auth } from '../config/firebase';
import { logger } from '../lib/logger';
import { getImpersonationHeaders } from './impersonationHeaders';
import { coerceKeywordRequest } from '../utils/keywordMatchRule';
import { currentUsFdaLabelId } from '../utils/usFdaCurrentLabel';
import type { LetterAISearchResponse, SearchHistoryEntry, SavedStarredList, SavedListDrug, SearchDescriptor, SectionConcept, DrugCounterparts, UsFdaCounterparts, UsFdaSubmissions, DrugSBAResponse, SuggestRow, CogniSummary } from '../types';
import type { LetterAISearchFilters } from '../hooks/useSearchQueryV2Letters';
import type { FDALetterSectionPagesResponse } from '../types/fdaLetter';
import type { StepToolCallsResponse } from '../types/workstream';

// =============================================================================
// Subscription Types
// =============================================================================

export interface SubscriptionResponse {
  id: number;
  type: string;
  name: string;
  is_active: boolean;
  created_at: string;
  last_notified_at: string | null;
}

export interface CreateSubscriptionBody {
  type: 'search' | 'label';
  name?: string;
  /** Required when type='search'. Pass the KeywordSearchRequest shape. */
  keyword_request?: Record<string, unknown>;
  /** Required when type='label'. */
  drug_id?: number;
}

export interface PatchSubscriptionBody {
  name?: string;
  is_active?: boolean;
}

export interface CheckSubscriptionResponse {
  subscribed: boolean;
  subscription_id: number | null;
  available: boolean;
}

export interface NotificationItem {
  key: string;
  kind: string;
  subscription_id: number;
  subscription_name: string;
  created_at: string;
  read: boolean;
  // label-change items
  drug_id?: number;
  display_name?: string;
  from_version?: string | number | null;
  to_version?: string | number | null;
  version_date?: string | null;
  region?: string;
  version_label?: string;
  from_version_label?: string | null;
  // search-subscription items
  matches?: { drug_id: number; display_name: string; region?: string; version_label?: string }[];
}

export interface TrainingVideo {
  id: string;
  title: string;
  description: string;
  duration: string;
  topic: string;
  thumbnail_url: string;
}

export interface NotificationsFeed {
  items: NotificationItem[];
  unread_count: number;
  has_more: boolean;
  emails_enabled: boolean;
}

export type MarkReadBody = { items: string[] } | { all: true };

export interface CachedQuery {
  key: string;
  query: string;
  cached_at: number;
  ttl_remaining: number;
  endpoint: string;
  result_count: number;
  params: Record<string, unknown>;
  mode?: string;        // CEPI rows only
  drug_label?: string;  // CEPI rows only
  turn_depth?: number;  // CEPI rows only
}

// Firebase Functions BFF endpoint (same Firebase project)
// This replaces direct Cloud Run calls and handles token verification
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

/**
 * Create authenticated axios instance
 */
const createAuthApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * REQUEST INTERCEPTOR
   * Automatically attach Firebase ID token to all requests
   */
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        // Get current user
        const currentUser = auth.currentUser;

        if (currentUser) {
          // Get fresh ID token (Firebase handles caching internally)
          // Set forceRefresh to false to use cached token when valid
          const idToken = await currentUser.getIdToken(false);

          // Attach token to Authorization header
          config.headers.Authorization = `Bearer ${idToken}`;
          logger.debug('Auth token attached to request:', { url: config.url });
        } else {
          logger.warn('No authenticated user, request sent without token:', { url: config.url });
        }
      } catch (error) {
        logger.error('Error getting auth token:', { error });
        // Continue with request even if token retrieval fails
        // The backend will return 401 if auth is required
      }

      // Admin impersonation (Task 7): spread the X-Impersonate-Org header
      // (if an admin session has one active) onto every axios request.
      // Unconditional — runs whether or not the Authorization branch above
      // succeeded, and getImpersonationHeaders() itself never throws.
      Object.assign(config.headers, getImpersonationHeaders());

      return config;
    },
    (error) => {
      logger.error('Request interceptor error:', { error });
      return Promise.reject(error);
    }
  );

  /**
   * RESPONSE INTERCEPTOR
   * Handle 401/403 errors and attempt token refresh
   */
  client.interceptors.response.use(
    (response) => {
      // Pass through successful responses
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
        _keepSessionOn403?: boolean;
      };

      // Check if error is 403 Forbidden (unauthorized email). Requests whose
      // 403 means "not allowed to see this resource" (not "not allowed in")
      // opt out with `_keepSessionOn403` and handle the 403 themselves.
      if (error.response?.status === 403 && !originalRequest?._keepSessionOn403) {
        logger.error('403 Forbidden - Email not authorized');
        // Sign out the user since they're not authorized
        try {
          await auth.signOut();
        } catch (signOutError) {
          logger.error('Error signing out:', { signOutError });
        }
        // Redirect to login with error message
        window.location.href = '/login?error=unauthorized';
        return Promise.reject(error);
      }

      // Check if error is 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        logger.warn('Received 401 Unauthorized, attempting token refresh...');

        // Mark request as retried to avoid infinite loops
        originalRequest._retry = true;

        try {
          const currentUser = auth.currentUser;

          if (currentUser) {
            // Force token refresh
            const newToken = await currentUser.getIdToken(true);
            logger.info('Token refreshed successfully, retrying request...');

            // Update Authorization header with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the original request
            return client(originalRequest);
          } else {
            logger.error('No authenticated user, redirecting to login...');
            // Redirect to login page
            window.location.href = '/login';
            return Promise.reject(error);
          }
        } catch (refreshError) {
          logger.error('Token refresh failed:', { refreshError });
          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // For other errors, just reject
      return Promise.reject(error);
    }
  );

  return client;
};

// Create singleton instance
const authApiClient = createAuthApiClient();

// Export raw axios client for ad-hoc admin/generic requests
export { authApiClient };

/**
 * Authenticated API service
 * All methods automatically include Firebase ID token
 */
export const authApi = {
  async getWorkstreamStepToolCalls(
    workstreamId: string,
    ordinal: number,
    includeHistory: boolean,
  ): Promise<StepToolCallsResponse> {
    const response = await authApiClient.get<StepToolCallsResponse>(
      `/v2/workstreams/${workstreamId}/steps/${ordinal}/tool-calls`,
      { params: { include_history: includeHistory } },
    );
    return response.data;
  },
  // =============================================================================
  // Search Endpoints (V2)
  // =============================================================================

  /**
   * Search with drug-level aggregation
   * Returns drug-level results by aggregating evidence chunks per drug
   */
  async searchV2Drugs(
    query: string,
    options?: {
      top_k?: number;
      drug_filter?: string;
      section_filter?: string[];
      marketingCategories?: string[];
      /** Multi-region filter. When non-empty, sent as ``regions`` array. */
      regions?: string[];
      /** Legacy single-region filter; ignored when ``regions`` is non-empty. */
      region?: string;
      page?: number;
      page_size?: number;
      signal?: AbortSignal;
      /** When true, includes draft label corpus results in the response. */
      include_label_negotiations?: boolean;
      /** Draft hit scope: 'all' (draft content + comments, default) | 'comments_only'. */
      draft_comment_scope?: 'all' | 'comments_only';
      /** Search corpus; only 'ha_reviews' adds body keys. */
      corpus?: 'labels' | 'ha_reviews';
      /** HA document types; sent only when ``corpus === 'ha_reviews'``. */
      review_doc_types?: string[];
    }
  ) {
    const config: any = {};
    if (options?.signal) {
      config.signal = options.signal;
    }
    const body: Record<string, unknown> = {
      query,
      top_k: options?.top_k ?? 50,
      drug_filter: options?.drug_filter,
      section_filter: options?.section_filter,
      marketing_categories: options?.marketingCategories ?? ['nda', 'bla', 'eu_authorised', 'eu_marketed'],
      page: options?.page ?? 1,
      page_size: options?.page_size ?? 50,
    };
    if (options?.regions?.length) {
      body['regions'] = options.regions;
    } else if (options?.region) {
      body['region'] = options.region;
    }
    if (options?.corpus === 'ha_reviews') {
      body['corpus'] = 'ha_reviews';
      if (options.review_doc_types?.length) body['review_doc_types'] = options.review_doc_types;
    } else if (options?.include_label_negotiations) {
      body['include_label_negotiations'] = true;
    }
    if (options?.draft_comment_scope && options.draft_comment_scope !== 'all') {
      body['draft_comment_scope'] = options.draft_comment_scope;
    }
    const response = await authApiClient.post('/v2/search/drugs', body, config);
    return response.data;
  },

  /**
   * Tag / name / manufacturer autocomplete suggestions (spec T2).
   * Debounced and cancelled by the caller (``useSearchSuggest``, 150ms +
   * AbortController) — this method just forwards ``signal`` through, same
   * pattern as ``searchV2Drugs``.
   *
   * ``regions`` must be sent as repeated ``regions=`` params — FastAPI's
   * ``list[str] = Query(None)`` shape — not axios's default
   * ``regions[]=...`` bracket form, which the backend silently ignores
   * (falling back to "every region" with no error). Same fix as
   * ``getFDALetterChunksBatch``'s ``ids`` param below: a ``paramsSerializer``
   * built on ``URLSearchParams.append``. No ``regions`` (absent or empty)
   * means every region, so that key is omitted entirely rather than sent
   * empty.
   */
  async searchSuggest(
    q: string,
    options?: { limit?: number; signal?: AbortSignal; regions?: string[] },
  ): Promise<SuggestRow[]> {
    const params: Record<string, unknown> = { q };
    if (options?.limit != null) params['limit'] = options.limit;
    if (options?.regions && options.regions.length > 0) {
      params['regions'] = options.regions;
    }
    const config: any = {
      params,
      paramsSerializer: {
        serialize: (p: Record<string, unknown>) => {
          const sp = new URLSearchParams();
          if (p['q'] != null) sp.append('q', String(p['q']));
          if (p['limit'] != null) sp.append('limit', String(p['limit']));
          const regions = p['regions'] as string[] | undefined;
          if (regions) for (const r of regions) sp.append('regions', r);
          return sp.toString();
        },
      },
    };
    if (options?.signal) config.signal = options.signal;
    const response = await authApiClient.get('/v2/search/suggest', config);
    return response.data;
  },

  /** On-request structured AI summary of the top results of a cached search. */
  async generateCogniSummary(body: { cache_key: string }): Promise<{ status: 'ready'; summary: CogniSummary }> {
    // A 403 here is a region gate on the cached rows, shown inline by the
    // summary panel: it must not sign the user out.
    const config: AxiosRequestConfig & { _keepSessionOn403: boolean } = { _keepSessionOn403: true };
    const response = await authApiClient.post('/v2/search/drugs/summary', body, config);
    return response.data;
  },

  async getSnippets(
    drugIds: number[],
    historicalIds: number[] = []
  ): Promise<{ snippets: Record<number, string> }> {
    // Backend expects: { drug_ids: [...], historical_ids: [...] }
    const response = await authApiClient.post('/search/snippets', {
      drug_ids: drugIds,
      historical_ids: historicalIds,
    });
    return response.data;
  },

  // =============================================================================
  // Drug Endpoints
  // =============================================================================

  async getDrug(drugId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/drugs/${drugId}`, signal ? { signal } : {});
    return response.data;
  },

  async getDrugCounterparts(drugId: number, signal?: AbortSignal): Promise<DrugCounterparts> {
    const response = await authApiClient.get(`/drugs/${drugId}/counterparts`, signal ? { signal } : {});
    return response.data;
  },

  // US-Drugs@FDA drug page. Both 403 unless US_FDA is visible to the user:
  // call only when `isUsFdaVisible(isAdmin)`.
  async getUsFdaCounterparts(drugId: number): Promise<UsFdaCounterparts> {
    const response = await authApiClient.get(`/drugs/${drugId}/us_fda_counterparts`);
    return response.data;
  },

  async getUsFdaSubmissions(usFdaDrugId: number): Promise<UsFdaSubmissions> {
    const response = await authApiClient.get(`/drugs/${usFdaDrugId}/us_fda_submissions`);
    return response.data;
  },

  /** The US_FDA product's current label id (null when none is current). */
  async getUsFdaCurrentLabelId(usFdaDrugId: number): Promise<number | null> {
    return currentUsFdaLabelId(await authApi.getUsFdaSubmissions(usFdaDrugId));
  },

  async getGeneric(genericId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/generics/${genericId}`, signal ? { signal } : {});
    return response.data;
  },

  // =============================================================================
  // Version Endpoints
  // =============================================================================

  async getVersionHistory(setId: string) {
    const response = await authApiClient.get(`/versions/history/${setId}`);
    return response.data;
  },

  async getVersion(versionId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/versions/${versionId}/details`, signal ? { signal } : {});
    return response.data;
  },

  async getViewerConfig(versionId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(
      `/versions/${versionId}/viewer-config`,
      signal ? { signal } : {}
    );
    return response.data as { server_url: string; config: any };
  },

  async uploadDraft(
    setId: string,
    file: File,
    changeSummary?: string
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('set_id', setId);
    if (changeSummary) {
      formData.append('change_summary', changeSummary);
    }

    const response = await authApiClient.post('/versions/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async publishVersion(versionId: number) {
    const response = await authApiClient.post(`/versions/${versionId}/publish`, null);
    return response.data;
  },

  async deleteVersion(versionId: number) {
    await authApiClient.delete(`/versions/${versionId}`);
  },

  async getDrugsWithDrafts(search?: string) {
    const params: Record<string, string> = {};
    if (search) params['search'] = search;
    const response = await authApiClient.get('/versions/drafts', { params });
    return response.data;
  },

  async getAdjacentVersions(versionId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/versions/${versionId}/adjacent`, signal ? { signal } : {});
    return response.data;
  },

  async compareVersionsDetailed(versionIdA: number, versionIdB: number) {
    const response = await authApiClient.get(`/versions/compare/${versionIdA}/${versionIdB}/detailed`);
    return response.data;
  },

  // =============================================================================
  // Drug-based Version Endpoints (for FDA historical versions in drugs table)
  // =============================================================================

  async compareDrugVersions(drugIdA: number, drugIdB: number) {
    const response = await authApiClient.get(`/drugs/compare/${drugIdA}/${drugIdB}`);
    return response.data;
  },

  // =============================================================================
  // 3-Table Architecture Endpoints (Dec 2025)
  // =============================================================================

  /**
   * Get ALL versions for a drug: FDA current + FDA historical + user drafts
   * This is the unified version list that enables cross-table comparison.
   */
  async getAllVersions(drugId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/drugs/${drugId}/all-versions`, signal ? { signal } : {});
    return response.data;
  },

  /**
   * Compare ANY two versions regardless of source table (polymorphic comparison)
   * Enables: FDA current ↔ FDA historical, FDA ↔ Draft, Historical ↔ Draft
   */
  async compareAnyVersions(
    sourceA: string,
    idA: number,
    sourceB: string,
    idB: number
  ) {
    const response = await authApiClient.get(`/compare/${sourceA}/${idA}/${sourceB}/${idB}`);
    return response.data;
  },

  /**
   * Get historical FDA version details (similar to getDrug but for historical versions)
   */
  async getHistoricalVersion(historicalId: number, signal?: AbortSignal) {
    const response = await authApiClient.get(`/historical/${historicalId}`, signal ? { signal } : {});
    return response.data;
  },

  // =============================================================================
  // Comment Endpoints
  // =============================================================================

  async getVersionComments(versionId: number) {
    const response = await authApiClient.get(`/versions/${versionId}/comments`);
    return response.data;
  },

  async addCommentNote(commentId: number, note: { note: string }) {
    const response = await authApiClient.patch(`/comments/${commentId}/note`, note);
    return response.data;
  },

  // =============================================================================
  // Drug Comparison Endpoints
  // =============================================================================

  /**
   * Compare 2-3 drug labels side by side with LLM-generated summary
   * @param query - The search query for context-aware comparison
   * @param drugIds - Array of 2-3 drug IDs to compare
   * @param sectionFilters - Optional specific sections to compare
   * @param maxSections - Optional max number of sections (default 5)
   */
  async compareDrugs(
    query: string,
    drugIds: number[],
    sectionFilters?: string[],
    maxSections?: number
  ) {
    const response = await authApiClient.post('/compare/drugs', {
      query,
      drug_ids: drugIds,
      section_filters: sectionFilters,
      max_sections: maxSections,
    });
    return response.data;
  },

  /**
   * Poll for async comparison summary result
   * @param taskId - The summary_task_id from compareDrugs response
   */
  async getComparisonSummary(taskId: string) {
    const response = await authApiClient.get(`/compare/summary/${taskId}`);
    return response.data;
  },

  // =============================================================================
  // Utility Endpoints
  // =============================================================================

  async healthCheck() {
    const response = await authApiClient.get('/health');
    return response.data;
  },

  async getCacheStats() {
    const response = await authApiClient.get('/cache/stats');
    return response.data;
  },

  async clearCache(): Promise<{ message: string; v1_cleared: number; v2_cleared: number }> {
    const response = await authApiClient.delete('/cache/clear');
    return response.data;
  },

  async clearCepiCache(): Promise<{ deleted: number }> {
    const response = await authApiClient.delete('/cache/cepi');
    return response.data;
  },

  async listCachedQueries(): Promise<{ queries: CachedQuery[]; total: number }> {
    const response = await authApiClient.get('/cache/queries');
    return response.data;
  },

  async deleteCachedQueries(keys: string[]): Promise<{ deleted: number }> {
    const response = await authApiClient.delete('/cache/queries', { data: { keys } });
    return response.data;
  },

  // =============================================================================
  // Draft Review Mode Endpoints (Jan 2026)
  // =============================================================================

  /**
   * Search draft comments with filtering for Draft Review Mode
   * Returns a 7-column table of comments, track changes, and outcomes
   */
  async searchDraftComments(params: {
    edit_type?: string;
    outcome_status?: string;
    author?: string;
    drug_id?: number;
    set_id?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await authApiClient.get('/search/draft-comments', { params });
    return response.data;
  },

  /**
   * Intelligent draft review search with intent extraction and LLM ranking.
   *
   * This is the enhanced search for Draft Review Mode that:
   * - Extracts intent from natural language queries
   * - Searches comments, track changes, AND draft label sections
   * - Uses LLM to score and rank results by relevance
   * - Returns properly scored results for the 7-column table
   *
   * @param params - Search parameters including query and filters
   * @returns DraftReviewSearchResponse with intent and scored results
   */
  async searchDraftReview(params: {
    query: string;
    edit_type?: string;
    outcome_status?: string;
    author?: string;
    drug_id?: number;
    set_id?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await authApiClient.post('/search/draft-review', params);
    return response.data;
  },

  /**
   * Update a comment's outcome status (user override or confirmation)
   * Sets outcome_suggested_by='user' to track manual overrides
   */
  async updateCommentOutcome(
    commentId: number,
    outcomeStatus: string,
    rationale?: string
  ) {
    // Send empty object instead of null - Firebase Functions body-parser can't handle null
    const response = await authApiClient.patch(`/comments/${commentId}/outcome`, {}, {
      params: {
        outcome_status: outcomeStatus,
        outcome_rationale: rationale,
      },
    });
    return response.data;
  },

  // =============================================================================
  // Keyword Search Endpoints
  // =============================================================================

  /**
   * Execute a keyword search across drug label sections.
   * Supports multi-row Boolean queries, exact/stemmed matching, proximity, etc.
   */
  async keywordSearch(rawRequest: Record<string, unknown>, signal?: AbortSignal) {
    // Label-text rows are Exact-only whatever a saved/restored request holds.
    const request = coerceKeywordRequest(rawRequest);
    const config: Record<string, unknown> = {};
    if (signal) config['signal'] = signal;
    // ``label_only`` is UI-only row state: keep the body identical for All and Label rows.
    const groups = request['groups'];
    const body = Array.isArray(groups)
      ? {
          ...request,
          groups: groups.map((g: { rows?: Record<string, unknown>[] }) => ({
            ...g,
            rows: g.rows?.map(({ label_only: _lo, ...r }) => r),
          })),
        }
      : request;
    const response = await authApiClient.post('/v2/keyword-search', body, config);
    return response.data;
  },

  /**
   * Get the list of available label sections for the section scope dropdown.
   */
  async getKeywordSearchSections() {
    const response = await authApiClient.get('/v2/keyword-search/sections');
    return response.data;
  },

  /**
   * Preview synonym expansion for a given keyword.
   */
  async expandSynonyms(keyword: string) {
    const response = await authApiClient.post('/v2/keyword-search/expand-synonyms', { keyword });
    return response.data;
  },

  /**
   * Submit user feedback.
   */
  async submitFeedback(data: { category: string; message: string; page_context?: string | undefined; search_context?: Record<string, unknown> | undefined }) {
    const response = await authApiClient.post('/feedback', data);
    return response.data;
  },

  /**
   * Upload a custom label (DOCX/PDF) for keyword search.
   */
  async uploadCustomLabel(
    file: File,
    brandName: string,
    genericName?: string | undefined,
    manufacturer?: string | undefined,
  ): Promise<{ drug_id: number; brand_name: string; sections_created: number; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('brand_name', brandName);
    if (genericName) {
      formData.append('generic_name', genericName);
    }
    if (manufacturer) {
      formData.append('manufacturer', manufacturer);
    }

    const response = await authApiClient.post('/v2/keyword-search/upload-label', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // =============================================================================
  // SBA (Summary Basis of Approval) Endpoints
  // =============================================================================

  // `cycle` is the Task 6 review-cycle query param
  // (`orig|all|unclassified|{fda_submission_id}`). Omit it to get
  // today's flat shape (byte-for-byte unchanged); pass it to opt into
  // the envelope when `SBA_CYCLE_VIEW_ENABLED` is on. `DrugSBAResponse`
  // is a union of both shapes (envelope fields optional) — this method
  // returns whichever one the backend sends without throwing.
  async getDrugSBAs(
    drugId: number,
    cycle?: string,
    signal?: AbortSignal,
  ): Promise<DrugSBAResponse> {
    const response = await authApiClient.get<DrugSBAResponse>(
      `/drugs/${drugId}/sbas`,
      {
        ...(cycle ? { params: { cycle } } : {}),
        ...(signal ? { signal } : {}),
      },
    );
    return response.data;
  },

  // =============================================================================
  // EPAR (EMA European Public Assessment Report) Endpoints
  // =============================================================================
  //
  // Mirror of the SBA methods above, hitting /api/epar/* and
  // /drugs/{drugId}/epars.  Returns the same shapes the SBA methods do
  // (the EPAR routes were cloned with identical response schemas) so
  // the viewer + DrugDetailPage can share rendering code.

  async getDrugEPARs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/epars`);
    return response.data;
  },

  // =============================================================================
  // UK MHRA PIL (Patient Information Leaflet) Endpoints
  // =============================================================================
  //
  // Mirrors the SBA + EPAR methods above, hitting /api/uk_pil/* and
  // /drugs/{drugId}/uk_pils.  Returns a similar shape but with
  // PIL-specific fields (pmid, pil_version_str, sor_pdf_uri,
  // ocr_pages, parse_method) replacing EPAR's
  // application_number / regulatory_action / etc.  The frontend
  // dropdown reads `doc_type_label` to render "Patient Information
  // Leaflet" entries; the viewer reads the shared shape directly.

  async getDrugUKPILs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/uk_pils`);
    return response.data;
  },

  // =============================================================================
  // CA Health Canada PM (Product Monograph) Endpoints
  // =============================================================================
  //
  // Mirrors the UK PIL methods above, hitting /api/ca_pm/* and
  // /drugs/{drugId}/ca_pms.  Returns a similar shape but with
  // PM-specific fields (pm_code, pm_version_str, sor_pdf_uri,
  // ocr_pages, pdf_page_count, total_sections) replacing the
  // PIL-specific ones.  The frontend dropdown reads `doc_type_label`
  // to render "Product Monograph" entries; the viewer reads the
  // shared shape directly.

  async getDrugCAPMs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/ca_pms`);
    return response.data;
  },

  /** Bilingual JP package-insert sections (English default + Japanese
   * original) for the JP main-label viewer + section toggle. ``docId`` renders one
   * specific package insert / edition of the drug; omitted = the current one. */
  async getDrugJPSections(drugId: number, docId?: number) {
    const params: Record<string, number> = {};
    if (docId != null) params['doc_id'] = docId;
    const response = await authApiClient.get(`/drugs/${drugId}/jp-sections`, { params });
    return response.data;
  },

  // ── JP main-label version compare ──
  async getDrugJpVersions(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/jp-versions`);
    return response.data;
  },
  /** Diff within ONE package insert (``lineage`` from getDrugJpVersions). */
  async getDrugJpVersionDiff(drugId: number, lineage?: string | null, fromVersion?: number, toVersion?: number) {
    const params: Record<string, number | string> = {};
    if (lineage != null) params['lineage'] = lineage;
    if (fromVersion != null) params['from_version'] = fromVersion;
    if (toVersion != null) params['to_version'] = toVersion;
    const response = await authApiClient.get(`/drugs/${drugId}/jp-version-diff`, { params });
    return response.data;
  },

  // ── AU (TGA PI) main-label version compare (single-language English PDF) ──
  async getDrugAuVersions(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/au-versions`);
    return response.data;
  },

  // ── EU SmPC unified version timeline (current + consolidated historical) ──
  async getDrugEuVersions(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/eu-versions`);
    return response.data;
  },
  async getDrugAuVersionDiff(drugId: number, fromVersion?: number, toVersion?: number) {
    const params: Record<string, number> = {};
    if (fromVersion != null) params['from_version'] = fromVersion;
    if (toVersion != null) params['to_version'] = toVersion;
    const response = await authApiClient.get(`/drugs/${drugId}/au-version-diff`, { params });
    return response.data;
  },

  // ── JP PMDA supplementary PDFs (jp_pdf family) ──
  async getJPPdfDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/jp_pdf_docs`);
    return response.data;
  },
  // ── CN NMPA supplementary PDFs (zh_pdf family; admin-gated server-side) ──
  async getZhPdfDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/zh_pdf_docs`);
    return response.data;
  },
  async getJPPdfToc(docId: number) {
    const response = await authApiClient.get(`/jp_pdf/documents/${docId}/toc`);
    return response.data;
  },
  async getJPPdfPages(docId: number) {
    const response = await authApiClient.get(`/jp_pdf/documents/${docId}/pages`);
    return response.data;
  },
  async getJPPdfSectionTranslation(docId: number, sectionId: number) {
    const response = await authApiClient.get(
      `/jp_pdf/documents/${docId}/sections/${sectionId}/translation`);
    return response.data;
  },
  // Hover viewer: translate one native-page block's Japanese text (the
  // frontend already has the block text from text_spans). Cached server-side.
  async translateJpPdfText(text: string, preserveHtml = false,
                           documentId?: number, sectionId?: number | null): Promise<string> {
    // documentId + sectionId enable reference-guided (translation-memory) hover
    // when the backend flag is on: the block is translated against its section's
    // authoritative stored English so the displayed word matches the searched
    // one. Omitted (legacy callers) → backend falls back to cold translation.
    const response = await authApiClient.post('/jp_pdf/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    // Reject a missing/null translation instead of collapsing it to '' — the old
    // `|| ''` turned a backend failure into a fake empty "success" that the hover
    // layer cached and never retried. A throw propagates to the caller's catch →
    // retry on next hover. A legitimately empty source still returns '' (a string).
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  /** EN search term → the label's source-language term (Swiss / German /
   *  Italian PDF in-doc search). '' on any failure — the caller just skips it. */
  async translateTermToSource(family: 'ch_pdf' | 'de_pdf' | 'it_pdf' | 'fr_html', en: string,
                              documentId: number): Promise<string> {
    const response = await authApiClient.post(`/${family}/translate-term`,
                                              { text: en, document_id: documentId });
    return (response.data?.src as string) || '';
  },

  /** Translate an English search word/phrase → its Japanese package-insert
   *  term so the viewer can box the Japanese equivalent on the page image.
   *  Returns '' on failure (caller skips the page highlight). */
  async translateTermToJa(en: string): Promise<string> {
    const response = await authApiClient.post('/jp_pdf/translate-term', { text: en });
    return response.data?.ja || '';
  },

  // Hover viewer (zh_pdf): translate one native-page block's Chinese text.
  // Mirror of ``translateJpPdfText`` bound to the /zh_pdf family (admin-gated
  // server-side). Cached server-side; reference-guided via document_id +
  // section_id when the backend flag is on.
  async translateZhPdfText(text: string, preserveHtml = false,
                           documentId?: number, sectionId?: number | null): Promise<string> {
    const response = await authApiClient.post('/zh_pdf/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    // Reject a missing/null translation instead of collapsing to '' — same
    // contract as translateJpPdfText so a backend failure retries on next hover.
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  // Swissmedic labels (ch_pdf): the shared bilingual /translate — the backend
  // picks the language spec from the document's source_lang, so document_id
  // is required.
  async translateChPdfText(text: string, preserveHtml = false,
                           documentId?: number, sectionId?: number | null): Promise<string> {
    const response = await authApiClient.post('/ch_pdf/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  async getDrugChPdfDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/ch_pdf_docs`);
    return response.data;
  },

  // BfArM labels (de_pdf): German only, same shared bilingual /translate.
  async translateDePdfText(text: string, preserveHtml = false,
                           documentId?: number, sectionId?: number | null): Promise<string> {
    const response = await authApiClient.post('/de_pdf/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  async getDePdfVersions(docId: number): Promise<{ versions: { vstr: string; date: string; docid: string }[] }> {
    const response = await authApiClient.get(`/de_pdf/documents/${docId}/versions`);
    return response.data;
  },

  async getDrugDePdfDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/de_pdf_docs`);
    return response.data;
  },

  // HPRA (ie_pdf, English) / AIFA (it_pdf, Italian) label PDFs: the de_pdf
  // listing shape; it_pdf has the same bilingual /translate as de_pdf.
  async getDrugLabelPdfDocs(drugId: number, family: 'ie_pdf' | 'it_pdf') {
    const response = await authApiClient.get(`/drugs/${drugId}/${family}_docs`);
    return response.data;
  },

  async translateItPdfText(text: string, preserveHtml = false,
                           documentId?: number, sectionId?: number | null): Promise<string> {
    const response = await authApiClient.post('/it_pdf/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  // ── US Drugs@FDA label PDFs (fda_label_pdf; admin-gated server-side: 403) ──
  // No translateFdaLabelPdfText counterpart: the source text is English, so
  // the backend mounts no /fda_label_pdf/translate route at all.

  async getFdaLabelPdfVersions(docId: number): Promise<{
    versions: { vstr: string; date: string; docid: string; label?: string }[]
  }> {
    const response = await authApiClient.get(`/fda_label_pdf/documents/${docId}/versions`);
    return response.data;
  },

  /** Cross-format compare: any two of {SPL XML, Drugs@FDA label PDF}.
   *  Returns logical CHANGES (change_id / operation / evidence / anchors),
   *  NOT the highlight-box payload the shared EuHistoricalCompare modal
   *  reads — which is why this has its own renderer. */
  async getCrossFormatCompare(params: {
    oldSource: 'spl_xml' | 'fda_pdf'; oldId: number;
    newSource: 'spl_xml' | 'fda_pdf'; newId: number;
    /** spl_xml only: drug_versions.version_number. Omitted selects that
     *  drug's CURRENT revision, and the response echoes which one it used —
     *  the endpoint never reads across revisions. */
    oldVersion?: number | null;
    newVersion?: number | null;
  }) {
    const response = await authApiClient.get('/fda_label_pdf/compare_cross_format', {
      params: {
        old_source: params.oldSource, old_id: params.oldId,
        new_source: params.newSource, new_id: params.newId,
        ...(params.oldVersion != null ? { old_version: params.oldVersion } : {}),
        ...(params.newVersion != null ? { new_version: params.newVersion } : {}),
      },
    });
    return response.data;
  },

  async getDrugFdaLabelPdfDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/fda_label_pdf_docs`);
    return response.data;
  },

  // ── FR ANSM BDPM labels (fr_html family; admin-gated server-side: 403) ──
  async getFrHtmlDocs(drugId: number) {
    const response = await authApiClient.get(`/fr_html/drugs/${drugId}/documents`);
    return response.data;
  },
  async getFrHtmlSections(docId: number) {
    const response = await authApiClient.get(`/fr_html/documents/${docId}/sections`);
    return response.data;
  },
  async translateFrHtmlText(text: string, preserveHtml = false,
                            documentId?: number, sectionId?: number | null): Promise<string> {
    const response = await authApiClient.post('/fr_html/translate',
      { text, preserve_html: preserveHtml, document_id: documentId, section_id: sectionId });
    const t = response.data?.translation;
    if (typeof t !== 'string') throw new Error('translation_unavailable');
    return t;
  },

  // =============================================================================
  // AU TGA (PI / CMI) Document Endpoints
  // =============================================================================
  //
  // Mirrors the CA PM methods above, hitting /api/au/* and
  // /drugs/{drugId}/au_docs.  Returns a similar shape but with
  // AU-specific fields; AU is a two-doc-type family so each document
  // carries a `doc_type` of "pi" or "cmi".  The frontend dropdown
  // reads `doc_type_label` to render "Australia TGA" entries; the
  // viewer reads the shared shape directly.

  async getDrugAUDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/au_docs`);
    return response.data;
  },

  // =============================================================================
  // EU EMA SmPC Document Endpoints
  // =============================================================================
  //
  // Mirrors the AU methods above, hitting /api/eu/* and
  // /drugs/{drugId}/eu_docs.  EU is a single-doc-type family ("smpc");
  // each document carries a `doc_type` of "smpc" and `doc_type_label`
  // renders as "EU SmPC".  The viewer reads the shared shape directly.

  async getDrugEUDocs(drugId: number) {
    const response = await authApiClient.get(`/drugs/${drugId}/eu_docs`);
    return response.data;
  },

  async listEPARDocuments(skip = 0, limit = 50) {
    const response = await authApiClient.get('/epar/documents', {
      params: { skip, limit },
    });
    return response.data;
  },

  async getEPARDocument(documentId: number) {
    const response = await authApiClient.get(`/epar/documents/${documentId}`);
    return response.data;
  },

  async getEPARSection(sectionId: number) {
    const response = await authApiClient.get(`/epar/sections/${sectionId}`);
    return response.data;
  },

  async getAllEPARSections(documentId: number) {
    const response = await authApiClient.get(`/epar/documents/${documentId}/all-sections`);
    return response.data;
  },

  async deleteEPARDocument(documentId: number) {
    const response = await authApiClient.delete(`/epar/documents/${documentId}`);
    return response.data;
  },

  async listSBADocuments(skip = 0, limit = 50) {
    const response = await authApiClient.get('/sba/documents', {
      params: { skip, limit },
    });
    return response.data;
  },

  async getSBADocument(documentId: number) {
    const response = await authApiClient.get(`/sba/documents/${documentId}`);
    return response.data;
  },

  async getSBASection(sectionId: number) {
    const response = await authApiClient.get(`/sba/sections/${sectionId}`);
    return response.data;
  },

  async getAllSBASections(documentId: number) {
    const response = await authApiClient.get(`/sba/documents/${documentId}/all-sections`);
    return response.data;
  },

  async deleteSBADocument(documentId: number) {
    const response = await authApiClient.delete(`/sba/documents/${documentId}`);
    return response.data;
  },

  async searchSBA(params: {
    query: string;
    document_id?: number;
    preferred_sections?: string[];
    top_k?: number;
  }) {
    const response = await authApiClient.post('/sba/search', params);
    return response.data;
  },

  // =============================================================================
  // FDA Letter Endpoints
  // =============================================================================

  async getDrugFDALetters(drugId: number) {
    const response = await authApiClient.get(`/fda-letters/by-drug/${drugId}`);
    return response.data;
  },

  async getFDALetterDetail(letterId: number) {
    const response = await authApiClient.get(`/fda-letters/documents/${letterId}`);
    return response.data;
  },

  async getFDALetterSection(sectionId: number) {
    const response = await authApiClient.get(`/fda-letters/sections/${sectionId}`);
    return response.data;
  },

  /** List rendered pages for an FDA-letter section (Phase 7 viewer).
   *
   * Returns ``{ section_id, pages: [...] }``.  An empty ``pages`` array
   * means the section is HTML-sourced — caller should render the
   * existing HTML path instead of the page-image viewer.
   */
  async getFDALetterSectionPages(sectionId: number): Promise<FDALetterSectionPagesResponse> {
    const response = await authApiClient.get(
      `/fda-letters/sections/${sectionId}/pages`,
    );
    return response.data as FDALetterSectionPagesResponse;
  },

  /** Fetch a single page image as a Blob (Phase 7 viewer).
   *
   * The backend endpoint requires Firebase auth (the BFF rejects un-tokened
   * requests to ``/fda-letters/*``), so we can't put the proxy URL directly
   * in an ``<img src>``.  Component wraps the blob with ``URL.createObjectURL``
   * and revokes on unmount.
   */
  async getFDALetterPageImage(pageId: number): Promise<Blob> {
    const response = await authApiClient.get(
      `/fda-letters/pages/${pageId}/image`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  /** Batch-fetch chunk metadata + bbox for the viewer highlight overlay.
   *
   * Max 200 ids per call (enforced by backend).  FastAPI's ``List[int] =
   * Query(...)`` expects repeated ``ids=1&ids=2`` style; using a
   * paramsSerializer keeps axios from doing the ``ids[]=...`` bracket
   * variant.
   */
  async getFDALetterChunksBatch(chunkIds: number[]) {
    const response = await authApiClient.get('/fda-letters/chunks/batch', {
      params: { ids: chunkIds },
      paramsSerializer: {
        serialize: (params: Record<string, unknown>) => {
          const sp = new URLSearchParams();
          const ids = params['ids'] as number[];
          for (const id of ids) sp.append('ids', String(id));
          return sp.toString();
        },
      },
    });
    return response.data;
  },

  /**
   * Cogni FDA letter AI search (hybrid FTS + vector retrieval, aggregated
   * to one row per letter).  Returns a dedicated ``LetterAISearchResponse``
   * shape distinct from the keyword path's ``KeywordLetterSearchResponse``
   * — no ``total_letters`` field; ``results.length`` is the count.
   *
   * Optional ``filters`` carry the three letter-corpus filter arrays
   * (Task 8).  Field names are snake_case to match the backend
   * ``LetterAISearchRequest`` schema; arrays default to ``[]`` (no
   * filter) when the caller omits them.
   */
  async fdaLetterAISearch(
    query: string,
    filters?: LetterAISearchFilters,
  ): Promise<LetterAISearchResponse> {
    // top_k is intentionally not exposed as a parameter — v1 has a single
    // page-size of 50 baked into the request. If a future v2 surfaces
    // pagination, add the parameter here AND thread it through
    // useSearchQueryV2Letters at the same time so the abstraction doesn't
    // leak. The backend default is also 50 (see LetterAISearchRequest), so
    // omitting top_k from the body is equivalent.
    //
    // Use ``.length`` so empty arrays are omitted from the wire body
    // (not just undefined). The backend treats omitted and ``[]``
    // identically, but a missing field keeps log lines cleaner.
    const body: Record<string, unknown> = { query };
    if (filters?.letter_types?.length) body['letter_types'] = filters.letter_types;
    if (filters?.letter_corpus?.length) body['letter_corpus'] = filters.letter_corpus;
    if (filters?.letter_roles?.length) body['letter_roles'] = filters.letter_roles;
    if (filters?.letter_divisions?.length) body['letter_divisions'] = filters.letter_divisions;
    const response = await authApiClient.post('/fda-letters/search', body);
    return response.data as LetterAISearchResponse;
  },

  // =============================================================================
  // Export Endpoints (Snapshots, Grid, Deck)
  // =============================================================================

  async createSnapshot(data: {
    query: string;
    drug_results: unknown[];
    filters?: Record<string, unknown>;
    intent?: unknown;
    evidence_chunks?: unknown[];
    summary?: string | null;
    metadata?: unknown;
    result_count?: number;
  }): Promise<{ share_token: string; url: string }> {
    const response = await authApiClient.post('/export/snapshot', data);
    return response.data;
  },

  async getSnapshot(shareToken: string) {
    const response = await authApiClient.get(`/export/snapshot/${shareToken}`);
    return response.data;
  },

  // ── Per-user search history (previous searches / replay) ──────────────────
  async listSearchHistory(limit = 30): Promise<{ entries: SearchHistoryEntry[] }> {
    const response = await authApiClient.get(`/search-history?limit=${limit}`);
    return response.data;
  },
  async recordSearchHistory(data: {
    mode: string;
    label: string;
    payload: Record<string, unknown>;
    result_count?: number;
  }): Promise<SearchHistoryEntry> {
    const response = await authApiClient.post('/search-history', data);
    return response.data;
  },
  async deleteSearchHistory(id: number): Promise<void> {
    await authApiClient.delete(`/search-history/${id}`);
  },
  async clearSearchHistory(): Promise<void> {
    await authApiClient.delete('/search-history');
  },

  // ── Saved starred lists (compare shortlists, per-user) ────────────────────
  async listSavedLists(limit = 50): Promise<{ lists: SavedStarredList[] }> {
    const response = await authApiClient.get(`/saved-lists?limit=${limit}`);
    return response.data;
  },
  async createSavedList(data: {
    name: string;
    drugs: SavedListDrug[];
    search_context?: SearchDescriptor | null;
  }): Promise<SavedStarredList> {
    const response = await authApiClient.post('/saved-lists', data);
    return response.data;
  },
  async updateSavedList(id: number, data: {
    drugs: SavedListDrug[];
    name?: string | null;
    search_context?: SearchDescriptor | null;
  }): Promise<SavedStarredList> {
    // PATCH (not PUT) — the BFF CORS allow-list permits PATCH; a PUT preflight
    // would be blocked. The endpoint treats a body with `drugs` as a full update.
    const response = await authApiClient.patch(`/saved-lists/${id}`, data);
    return response.data;
  },
  async renameSavedList(id: number, name: string): Promise<void> {
    await authApiClient.patch(`/saved-lists/${id}`, { name });
  },
  async deleteSavedList(id: number): Promise<void> {
    await authApiClient.delete(`/saved-lists/${id}`);
  },

  // ── Section-concept mapping (cross-geo section compare) ───────────────────
  async listSectionConcepts(): Promise<{ concepts: SectionConcept[] }> {
    const response = await authApiClient.get('/section-concepts');
    return response.data;
  },

  async compareGrid(drugIds: number[], sectionFilters?: string[]) {
    const response = await authApiClient.post('/export/compare-grid', {
      drug_ids: drugIds,
      section_filters: sectionFilters,
    });
    return response.data;
  },

  // Non-US section matrix: aligns one corpus's docs by section_number_local,
  // content assembled (plain <p>) from chunks. ``documentIds`` are corpus doc ids.
  async compareGridRegulatory(corpus: string, documentIds: number[]) {
    const response = await authApiClient.post('/export/compare-grid-regulatory', {
      corpus,
      document_ids: documentIds,
    });
    return response.data;
  },

  async generateReport(data: {
    title: string;
    drug_names: string[];
    conversation_messages: { role: string; content: string }[];
  }): Promise<Blob> {
    const response = await authApiClient.post('/export/generate-report', data, {
      responseType: 'blob',
    });
    return response.data;
  },

  /** @deprecated Use generateReport() instead */
  async generateDeck(data: {
    title: string;
    drug_names: string[];
    conversation_messages: { role: string; content: string }[];
  }): Promise<Blob> {
    return this.generateReport(data);
  },

  // =============================================================================
  // Subscription Endpoints
  // =============================================================================

  /**
   * Create a new subscription (search or label).
   * POST /subscriptions
   * Returns 201 SubscriptionResponse; 409 if already subscribed; 400 if the region/source is not allowed.
   */
  async createSubscription(body: CreateSubscriptionBody): Promise<SubscriptionResponse> {
    const response = await authApiClient.post('/subscriptions', body);
    return response.data as SubscriptionResponse;
  },

  /**
   * List all subscriptions for the current user.
   * GET /subscriptions → SubscriptionResponse[]
   */
  async listSubscriptions(): Promise<SubscriptionResponse[]> {
    const response = await authApiClient.get('/subscriptions');
    return response.data as SubscriptionResponse[];
  },

  /**
   * Delete (hard-delete) a subscription by id.
   * DELETE /subscriptions/{id} → {deleted:true}
   */
  async deleteSubscription(id: number): Promise<{ deleted: boolean }> {
    const response = await authApiClient.delete(`/subscriptions/${id}`);
    return response.data as { deleted: boolean };
  },

  /**
   * Patch a subscription (rename or toggle is_active).
   * PATCH /subscriptions/{id}
   */
  async patchSubscription(id: number, body: PatchSubscriptionBody): Promise<SubscriptionResponse> {
    const response = await authApiClient.patch(`/subscriptions/${id}`, body);
    return response.data as SubscriptionResponse;
  },

  /**
   * Check whether the current user is subscribed to a specific label.
   * GET /subscriptions/check?type=label&drug_id=<id>
   */
  async checkSubscription(params: { type: 'label'; drug_id: number }): Promise<CheckSubscriptionResponse> {
    const response = await authApiClient.get('/subscriptions/check', { params });
    return response.data as CheckSubscriptionResponse;
  },

  /** Training catalog visible to the current user. GET /training/videos */
  async getTrainingVideos(): Promise<TrainingVideo[]> {
    const response = await authApiClient.get('/training/videos');
    return response.data.videos as TrainingVideo[];
  },

  /** Short-lived signed player URL for one video. GET /training/videos/{id}/embed */
  async getTrainingEmbedUrl(id: string): Promise<string> {
    const response = await authApiClient.get(`/training/videos/${encodeURIComponent(id)}/embed`);
    return response.data.embed_url as string;
  },

  /**
   * Notifications feed for the current user.
   * GET /subscriptions/notifications
   */
  async getNotifications(): Promise<NotificationsFeed> {
    const response = await authApiClient.get('/subscriptions/notifications');
    return response.data as NotificationsFeed;
  },

  /**
   * Mark notifications read (by key, or all).
   * POST /subscriptions/notifications/read → {updated, unread_count}
   */
  async markNotificationsRead(body: MarkReadBody): Promise<{ updated: number; unread_count: number }> {
    const response = await authApiClient.post('/subscriptions/notifications/read', body);
    return response.data as { updated: number; unread_count: number };
  },

  /**
   * Public (no auth) — deactivate a subscription by manage_token.
   * GET /subscriptions/unsubscribe/{token}
   * Note: uses the BFF's apiProxy, which skips auth for paths matching the
   * PUBLIC_PATH_PATTERNS list (Task 6.6 adds /subscriptions/unsubscribe/* there).
   */
  async unsubscribe(token: string): Promise<{ unsubscribed: boolean }> {
    const encodedToken = encodeURIComponent(token);
    // Use the raw authApiClient so the interceptor still attaches a token if
    // one is present, but the BFF won't reject the request when no token is
    // available (public path).  The backend ignores auth on this endpoint.
    const response = await authApiClient.get(`/subscriptions/unsubscribe/${encodedToken}`);
    return response.data as { unsubscribed: boolean };
  },
};

export default authApi;
